import { Link } from "react-router-dom";

type RightsStackVariant = "full" | "compact" | "detail";

interface NftRightsStackProps {
  active?: boolean;
  className?: string;
  pendingWethLabel?: string;
  showHeader?: boolean;
  showReferral?: boolean;
  variant?: RightsStackVariant;
}

interface RightsItem {
  accent: "green" | "weth" | "gold" | "violet" | "cyan";
  body: string;
  cta?: { label: string; to: string };
  label: string;
  title: string;
}

const RIGHTS_ITEMS: RightsItem[] = [
  {
    accent: "weth",
    label: "01 / HOLD",
    title: "Share trading fees",
    body: "Hold an active Ape and share the official pool trading-fee stream with other active NFT holders. The hook collects WETH, and pending WETH belongs to the current NFT owner.",
    cta: { label: "Claim fees", to: "/fees" }
  },
  {
    accent: "green",
    label: "02 / BURN",
    title: "CAPE burn claim",
    body: "After finalization, burning claims 5,000 CAPE base plus a capped dynamic bonus. At the initial LP ratio, 5,000 CAPE maps to 0.005 ETH before market movement; bonus can add more while the pool lasts.",
    cta: { label: "Burn page", to: "/burn" }
  },
  {
    accent: "gold",
    label: "03 / LP",
    title: "Own part of the LP",
    body: "Launch liquidity locks in UNCX for 90 days. After unlock, each unburned and unclaimed Ape can receive a split v4 LP position, meaning the owner directly holds that liquidity position.",
    cta: { label: "LP page", to: "/lp" }
  },
  {
    accent: "violet",
    label: "04 / TRADE",
    title: "Transferable rights",
    body: "The Ape is the bearer receipt. Transfer it and active WETH and future LP rights move with the token. Burn it and those future rights stop.",
    cta: { label: "Gallery", to: "/gallery" }
  }
];

const REFERRAL_ITEM: RightsItem = {
  accent: "cyan",
  label: "BONUS / INVITE",
  title: "Holder-gated invite reward",
  body: "After you mint, create an invite link. The 10% reward accrues to the inviter wallet after successful referred mints; it is wallet-level, not a listed-NFT entitlement.",
  cta: { label: "Invite after mint", to: "/mint" }
};

export default function NftRightsStack({
  active,
  className = "",
  pendingWethLabel,
  showHeader = true,
  showReferral = false,
  variant = "full"
}: NftRightsStackProps) {
  const items = showReferral ? [...RIGHTS_ITEMS, REFERRAL_ITEM] : RIGHTS_ITEMS;

  return (
    <section className={`nft-rights nft-rights--${variant} ${className}`.trim()} aria-labelledby={showHeader ? "nft-rights-title" : undefined}>
      {showHeader && (
        <header className="nft-rights__header">
          <span className="eyebrow">// one nft, multiple rights</span>
          <h2 id="nft-rights-title" className="nft-rights__title">What one CODE APE represents</h2>
          <p className="nft-rights__lede">
            A CODE APE tokenId is the receipt layer. It can hold WETH fee rights, burn into CAPE, preserve a later LP claim, and transfer those active rights to the next owner.
          </p>
        </header>
      )}

      <div className="nft-rights__grid">
        {items.map((item) => (
          <RightsCard key={item.label} item={item} active={active} pendingWethLabel={pendingWethLabel} />
        ))}
      </div>

      <aside className="nft-rights__note">
        <strong>Simple game:</strong> hold to keep trading-fee and LP optionality; burn to take CAPE and leave future fees and unclaimed LP behind. Referral rewards are wallet-level and separate from tokenId rights.
      </aside>
    </section>
  );
}

function RightsCard({ item, active, pendingWethLabel }: { active?: boolean; item: RightsItem; pendingWethLabel?: string }) {
  const status = rightsStatus(item.title, active, pendingWethLabel);

  return (
    <article className={`nft-rights__card nft-rights__card--${item.accent}`}>
      <div className="nft-rights__card-label">{item.label}</div>
      <div className="nft-rights__card-top">
        <h3>{item.title}</h3>
        <span>{status}</span>
      </div>
      <p>{item.body}</p>
      {item.cta && <Link to={item.cta.to}>{item.cta.label} →</Link>}
    </article>
  );
}

function rightsStatus(title: string, active?: boolean, pendingWethLabel?: string): string {
  if (title === "Holder-gated invite reward") return "Wallet-level";
  if (active === false) {
    if (title === "Share trading fees") return "Inactive";
    if (title === "CAPE burn claim") return "Already burned";
    if (title === "Own part of the LP") return "Forfeited if unclaimed";
    return "Historical artifact";
  }
  if (title === "Share trading fees") return pendingWethLabel ? pendingWethLabel : "Active receipt";
  if (title === "CAPE burn claim") return "Optional exit";
  if (title === "Own part of the LP") return "Requires unburned NFT";
  return "Moves with owner";
}
